import { api } from '../api';

import type {
  ConfirmKnowledgeHubStatusRequest,
  GetKnowledgeHubStepsStatusesRequest,
  GetKnowledgeHubStepsStatusesResponse,
  KnowledgeHubStepsName,
  KnowledgeHubStepsStatus,
} from 'api/endpoints/knowledge-hub/knowledge-hub.types';

export const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getKnowledgeHubStepsStatuses: build.query<
      GetKnowledgeHubStepsStatusesResponse,
      GetKnowledgeHubStepsStatusesRequest
    >({
      query: ({ organizationId, stepNames }) => {
        const stepsParams = stepNames
          ?.map((stepName) => `steps[]=${stepName}`)
          .join('&');

        return {
          url: `organizations/${organizationId}/steps${
            stepNames?.length ? `?${stepsParams}` : ''
          }`,
          method: 'GET',
        };
      },
      providesTags: ['KnowledgeHubStepsStatus'],
      async onQueryStarted(
        { organizationId, stepNames },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: step } = await queryFulfilled;

          dispatch(
            extendedApi.util.updateQueryData(
              'getKnowledgeHubStepsStatuses',
              { organizationId },
              (draft) => {
                if (stepNames && stepNames.length) {
                  for (const stepName of stepNames) {
                    draft.steps[stepName].status = step.steps[stepName].status;
                  }
                } else {
                  draft.steps = step.steps;
                }
              },
            ),
          );
        } catch {
          // Query errors handled by RTK Query
        }
      },
    }),
    confirmKnowledgeHubStep: build.mutation<
      void,
      ConfirmKnowledgeHubStatusRequest
    >({
      query: ({ stepName }) => ({
        url: `/company/confirm_step.json`,
        method: 'PATCH',
        params: {
          step: stepName,
        },
      }),
      async onQueryStarted(
        { organizationId, stepName },
        { dispatch, queryFulfilled },
      ) {
        await queryFulfilled;

        dispatch(
          updateKnowledgeHubStepStatus({
            organizationId,
            stepName,
            status: 'done',
          }),
        );
      },
      invalidatesTags: ['KnowledgeHubStepsStatus'],
    }),
  }),
});

export const invalidatesKnowledgeHubStepsStatuses = () =>
  extendedApi.util.invalidateTags(['KnowledgeHubStepsStatus']);

export const updateKnowledgeHubStepStatus = ({
  organizationId,
  stepName,
  status,
}: {
  organizationId: number;
  stepName: KnowledgeHubStepsName;
  status: KnowledgeHubStepsStatus;
}) => {
  return extendedApi.util.updateQueryData(
    'getKnowledgeHubStepsStatuses',
    { organizationId },
    (draft) => {
      draft.steps[stepName].status = status;
    },
  );
};

export const {
  useGetKnowledgeHubStepsStatusesQuery,
  useConfirmKnowledgeHubStepMutation,
} = extendedApi;
